import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function PurchaseOrders() {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    poNumber: true,
    supplier: true,
    orderDate: true,
    expectedDate: true,
    status: true,
    linkedTo: true,
    amount: true
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  useEffect(() => {
    fetchPOs();
    fetchSuppliers();
    fetchWarehouses();
    loadColumnPreferences();
  }, []);
  
  const fetchPOs = async () => {
    try {
      const response = await apiFetch('/api/purchase-orders');
      const data = await response.json();
      setPos(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching POs:', error);
      setLoading(false);
    }
  };
  
  const fetchSuppliers = async () => {
    try {
      const response = await apiFetch('/api/suppliers');
      const data = await response.json();
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };
  
  const fetchWarehouses = async () => {
    try {
      const response = await apiFetch('/api/warehouses');
      const data = await response.json();
      setWarehouses(data);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };
  
  const loadColumnPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/purchase-orders');
      const data = await response.json();
      if (data.preferences?.visibleColumns) {
        setVisibleColumns(data.preferences.visibleColumns);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };
  
  const saveColumnPreferences = async (columns) => {
    try {
      await apiFetch('/api/preferences/default/purchase-orders', {
        method: 'PUT',
        body: JSON.stringify({ preferences: { visibleColumns: columns } })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };
  
  const toggleColumn = (column) => {
    const updated = { ...visibleColumns, [column]: !visibleColumns[column] };
    setVisibleColumns(updated);
    saveColumnPreferences(updated);
  };
  
  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      sent: 'bg-blue-500',
      received: 'bg-green-600',
      cancelled: 'bg-red-600',
      overdue: 'bg-orange-600',
      delayed: 'bg-amber-500'
    };
    return colors[status] || 'bg-gray-500';
  };
  
  const getStatusIcon = (status) => {
    const icons = {
      draft: '📝',
      sent: '📤',
      received: '✅',
      cancelled: '❌',
      overdue: '⚠️',
      delayed: '⏳'
    };
    return icons[status] || '📦';
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };
  
  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateString);
    target.setHours(0, 0, 0, 0);
    const diff = Math.floor((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  };
  
  const isOverdue = (po) => {
    if (po.status === 'received' || po.status === 'cancelled') return false;
    if (!po.expected_date) return false;
    return getDaysUntil(po.expected_date) < 0;
  };
  
  const isCritical = (po) => {
    return po.job_number || po.sales_order_number;
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSupplierFilter('all');
    setWarehouseFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  };
  
  const toggleSelectAll = () => {
    if (selectedPOs.length === filteredPOs.length) {
      setSelectedPOs([]);
    } else {
      setSelectedPOs(filteredPOs.map(po => po.id));
    }
  };
  
  const toggleSelectPO = (id) => {
    if (selectedPOs.includes(id)) {
      setSelectedPOs(selectedPOs.filter(poId => poId !== id));
    } else {
      setSelectedPOs([...selectedPOs, id]);
    }
  };
  
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedPOs.length === 0) {
      window.alert('Please select POs to update');
      return;
    }
    
    if (!window.confirm(`Mark ${selectedPOs.length} PO(s) as ${newStatus}?`)) {
      return;
    }
    
    try {
      await Promise.all(
        selectedPOs.map(id =>
          apiFetch(`/api/purchase-orders/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status: newStatus })
          })
        )
      );
      
      window.alert(`${selectedPOs.length} PO(s) updated to ${newStatus}`);
      setSelectedPOs([]);
      setShowBulkActions(false);
      fetchPOs();
    } catch (error) {
      console.error('Error updating POs:', error);
      window.alert('Failed to update some POs');
    }
  };
  
  const exportToCSV = () => {
    const dataToExport = selectedPOs.length > 0 
      ? filteredPOs.filter(po => selectedPOs.includes(po.id))
      : filteredPOs;
    
    if (dataToExport.length === 0) {
      window.alert('No data to export');
      return;
    }
    
    const headers = ['PO Number', 'Supplier', 'Order Date', 'Expected Date', 'Status', 'Amount', 'Linked To'];
    const rows = dataToExport.map(po => [
      po.po_number,
      po.supplier_name,
      po.order_date,
      po.expected_date || '',
      po.status,
      po.total_amount,
      po.job_number ? `Job #${po.job_number}` : po.sales_order_number ? `SO #${po.sales_order_number}` : ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  
  const filteredPOs = pos.filter(po => {
    const matchesSearch = 
      po.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.sales_order_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
    const matchesSupplier = supplierFilter === 'all' || po.supplier_id === parseInt(supplierFilter);
    const matchesWarehouse = warehouseFilter === 'all' || po.warehouse_id === parseInt(warehouseFilter);
    
    const matchesDateFrom = !dateFrom || new Date(po.order_date) >= new Date(dateFrom);
    const matchesDateTo = !dateTo || new Date(po.order_date) <= new Date(dateTo);
    
    const matchesAmountMin = !amountMin || parseFloat(po.total_amount) >= parseFloat(amountMin);
    const matchesAmountMax = !amountMax || parseFloat(po.total_amount) <= parseFloat(amountMax);
    
    return matchesSearch && matchesStatus && matchesSupplier && matchesWarehouse && 
           matchesDateFrom && matchesDateTo && matchesAmountMin && matchesAmountMax;
  });
  
  // Calculate stats
  const overduePOs = pos.filter(isOverdue);
  const criticalPOs = pos.filter(po => isCritical(po) && po.status === 'sent');
  
  const stats = {
    draft: pos.filter(po => po.status === 'draft').length,
    sent: pos.filter(po => po.status === 'sent').length,
    received: pos.filter(po => po.status === 'received').length,
    overdue: overduePOs.length,
    critical: criticalPOs.length,
    totalValue: pos.reduce((sum, po) => sum + parseFloat(po.total_amount || 0), 0),
    inTransitValue: pos.filter(po => po.status === 'sent').reduce((sum, po) => sum + parseFloat(po.total_amount || 0), 0)
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="Purchase Orders"
        subtitle={`${filteredPOs.length} orders`}
        backButton
        onBack={() => navigate('/operations')}
        backTitle="Back to Operations"
        sticky
        actionButton={
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/purchase-orders/dashboard')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
            >
              📊 Dashboard
            </button>
            <button
              onClick={() => navigate('/purchase-orders/new')}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
            >
              ➕ Add Purchase Order
            </button>
          </div>
        }
      />
      
      <div className="p-8">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Draft</div>
            <div className="text-2xl font-bold text-gray-900">{stats.draft}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Sent</div>
            <div className="text-2xl font-bold text-blue-600">{stats.sent}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Received</div>
            <div className="text-2xl font-bold text-green-600">{stats.received}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 border-l-4 border-red-500">
            <div className="text-sm text-gray-600 mb-1">Overdue</div>
            <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 border-l-4 border-orange-500">
            <div className="text-sm text-gray-600 mb-1">Critical</div>
            <div className="text-2xl font-bold text-orange-600">{stats.critical}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">In Transit</div>
            <div className="text-xl font-bold text-blue-600">{formatCurrency(stats.inTransitValue)}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Total Value</div>
            <div className="text-xl font-bold text-gray-900">{formatCurrency(stats.totalValue)}</div>
          </div>
        </div>
        
        {/* Filters & Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <input
              type="text"
              placeholder="Search POs, suppliers, jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium whitespace-nowrap"
            >
              {showAdvancedFilters ? '▼' : '▶'} Advanced
            </button>
            
            {(searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || warehouseFilter !== 'all' || dateFrom || dateTo || amountMin || amountMax) && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium whitespace-nowrap"
              >
                ✕ Clear
              </button>
            )}
            
            {/* Column Selector */}
            <div className="relative">
              <button
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium whitespace-nowrap"
              >
                ⚙️ Columns
              </button>
              
              {showColumnSelector && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <div className="p-3 space-y-2">
                    {Object.keys(visibleColumns).map(column => (
                      <label key={column} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={visibleColumns[column]}
                          onChange={() => toggleColumn(column)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-sm capitalize">
                          {column.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Suppliers</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.company_name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse</label>
                <select
                  value={warehouseFilter}
                  onChange={(e) => setWarehouseFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Warehouses</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Min (£)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Max (£)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                  placeholder="10000.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Bulk Actions Bar */}
        {selectedPOs.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="font-medium text-blue-900">
              {selectedPOs.length} PO(s) selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkStatusChange('sent')}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
              >
                Mark as Sent
              </button>
              <button
                onClick={() => handleBulkStatusChange('cancelled')}
                className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={exportToCSV}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
              >
                📥 Export Selected
              </button>
              <button
                onClick={() => setSelectedPOs([])}
                className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}
        
        {/* Action Bar */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            Showing {filteredPOs.length} of {pos.length} purchase orders
          </div>
          <button
            onClick={exportToCSV}
            className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
          >
            📥 Export to CSV
          </button>
        </div>
        
        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-300 border-b border-gray-400">
              <tr>
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedPOs.length === filteredPOs.length && filteredPOs.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded text-blue-600"
                  />
                </th>
                {visibleColumns.poNumber && (
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-900">
                    PO Number
                  </th>
                )}
                {visibleColumns.supplier && (
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-900">
                    Supplier
                  </th>
                )}
                {visibleColumns.orderDate && (
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-900">
                    Order Date
                  </th>
                )}
                {visibleColumns.expectedDate && (
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-900">
                    Expected
                  </th>
                )}
                {visibleColumns.status && (
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-900">
                    Status
                  </th>
                )}
                {visibleColumns.linkedTo && (
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-900">
                    Linked To
                  </th>
                )}
                {visibleColumns.amount && (
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-900">
                    Amount
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="20" className="px-6 py-12 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredPOs.length === 0 ? (
                <tr>
                  <td colSpan="20" className="px-6 py-12 text-center text-gray-500">
                    {searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || warehouseFilter !== 'all' || dateFrom || dateTo || amountMin || amountMax
                      ? 'No purchase orders match your filters' 
                      : 'No purchase orders yet. Click "Add Purchase Order" to get started.'}
                  </td>
                </tr>
              ) : (
                filteredPOs.map((po, index) => (
                  <tr
                    key={po.id}
                    className={`hover:bg-blue-200 cursor-pointer transition ${
                      index % 2 === 1 ? 'bg-blue-100' : 'bg-white'
                    }`}
                  >
                    <td 
                      className="px-3 py-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectPO(po.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPOs.includes(po.id)}
                        onChange={() => toggleSelectPO(po.id)}
                        className="rounded text-blue-600"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    {visibleColumns.poNumber && (
                      <td 
                        className="px-3 py-2 text-gray-900 font-medium"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        <div className="flex items-center gap-2">
                          {po.po_number}
                          {isOverdue(po) && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded font-semibold">
                              OVERDUE
                            </span>
                          )}
                          {isCritical(po) && po.status === 'sent' && (
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded font-semibold">
                              CRITICAL
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    {visibleColumns.supplier && (
                      <td 
                        className="px-3 py-2 text-gray-900"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        {po.supplier_name}
                      </td>
                    )}
                    {visibleColumns.orderDate && (
                      <td 
                        className="px-3 py-2 text-gray-900"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        {formatDate(po.order_date)}
                      </td>
                    )}
                    {visibleColumns.expectedDate && (
                      <td 
                        className="px-3 py-2 text-gray-900"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        {formatDate(po.expected_date)}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td 
                        className="px-3 py-2"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(po.status)} text-white`}>
                          {getStatusIcon(po.status)} {po.status?.toUpperCase()}
                        </span>
                      </td>
                    )}
                    {visibleColumns.linkedTo && (
                      <td 
                        className="px-3 py-2 text-gray-900"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        {po.job_number ? (
                          <span className="text-blue-600">Job #{po.job_number}</span>
                        ) : po.sales_order_number ? (
                          <span className="text-green-600">SO #{po.sales_order_number}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    )}
                    {visibleColumns.amount && (
                      <td 
                        className="px-3 py-2 text-gray-900 font-semibold text-right"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        {formatCurrency(po.total_amount)}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PurchaseOrders;
